import { ServiceStackTypes } from '@zerops/models/service-stack';
import { ScalingTypes } from '../autoscaling-form-field/autoscaling-form.model';

const CPU_TEXT = ' vertical cores scaling';
const RAM_TEXT = ' vertical RAM scaling';
const DISC_TEXT = ' vertical Disk scaling';
const CONTAINER_TEXT = ' horizontal scaling';
const CPU_SCALE_TEXT = 'Zerops automatically adjusts the number of cores based on a modifiable threshold of free resources. Unless your application can handle or utilize more than one CPU core, you should limit the scaling range to a single core. When container scaling is active and Zerops reaches the maximum number of cores, it will begin to scale up by adding new containers.';
const GENERAL_DISC_TEXT = 'The disk space is automatically scaled up or down without restarting the process. You, however, should not use the local container disk to store your files as the containers are volatile, and their data are not mirrored across the cluster nor backup-ed. Use one of the Zerops storage services to keep your files permanently.'
const DISC_SPACE_TEXT = 'The disk space is automatically scaled up or down as the data on the disk increases or decreases.';
const LOCAL_STORAGE_DISC_TEXT = 'The disk space is the size of the volume itself. It grows automatically as the data on it grows, but it never shrinks back — raising the maximum is a one-way step.';
const DISC_SCALING_TEXT = 'The disk space is automatically scaled up or down as the database replicated data increases or decreases. You should not use the local container disk to store non-database data as they are not mirrored across the cluster nor backup-ed.';
const RAM_ADJUST_TEXT = 'Zerops automatically adjusts the amount of RAM within a user-defined range based on a modifiable threshold of free resources. If your application\'s RAM usage is consistent and predictable, setting a specific upper limit on RAM can help manage resource allocation efficiently. When RAM scaling is active and the system approaches the maximum allocated RAM, Zerops focuses on optimizing resource usage within the existing configuration, without adding new containers.';
const GENERAL_CONTAINER_TEXT = (name: string) => {
  return `The best way to scale ${name} is by adding and removing containers, start with at least two to achieve high availability. Remember, however, that your application needs to be prepared for this.`
}

export const SCALING_DIALOG_DATA: Record<
  string,
  Record<string, { title: string; desc: string }>
> = {
  [ServiceStackTypes.NodeJs]: {
    [ScalingTypes.Cpu]: {
      title: 'Node.js' + CPU_TEXT,
      desc: 'Node.js is a single thread runtime, and unless you use tools like <a href="https://pm2.keymetrics.io/" target="_blank">pm2</a>, you should use a single core.',
    },
    [ScalingTypes.Ram]: {
      title: 'Node.js' + RAM_TEXT,
      desc: "Node.js is not able to scale up memory on the fly without restarting the process, so it's advisable to start with enough RAM to run your most expensive operations (together with --max-old-space-size switch) and set max to the same value.",
    },
    [ScalingTypes.Disc]: {
      title: 'Node.js' + DISC_TEXT,
      desc: GENERAL_DISC_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'Node.js' + CONTAINER_TEXT,
      desc: GENERAL_CONTAINER_TEXT('Node.js')
    },
  },

  [ServiceStackTypes.Bun]: {
    [ScalingTypes.Cpu]: {
      title: 'Bun' + CPU_TEXT,
      desc: 'Bun is a single thread runtime, and unless you use tools like <a href="https://pm2.keymetrics.io/" target="_blank">pm2</a>, you should use a single core.',
    },
    [ScalingTypes.Ram]: {
      title: 'Bun' + RAM_TEXT,
      desc: "Bun is not able to scale up memory on the fly without restarting the process, so it's advisable to start with enough RAM to run your most expensive operations (together with --max-old-space-size switch) and set max to the same value.",
    },
    [ScalingTypes.Disc]: {
      title: 'Bun' + DISC_TEXT,
      desc: GENERAL_DISC_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'Bun' + CONTAINER_TEXT,
      desc: GENERAL_CONTAINER_TEXT('Bun')
    },
  },

  [ServiceStackTypes.Deno]: {
    [ScalingTypes.Cpu]: {
      title: 'Deno vertical CPU cores scaling',
      desc: 'Deno uses a single-threaded event loop model, similar to Node.js, so in most cases, a single core is sufficient. However, certain workloads can benefit from multiple instances across multiple cores.',
    },
    [ScalingTypes.Ram]: {
      title: 'Deno' + RAM_TEXT,
      desc: 'Deno does not dynamically scale memory on the fly without restarting the process. It\'s advisable to allocate enough RAM to handle your application\'s peak memory usage and set appropriate limits.'
    },
    [ScalingTypes.Disc]: {
      title: 'Deno' + DISC_TEXT,
      desc: GENERAL_DISC_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'Deno' + CONTAINER_TEXT,
      desc: GENERAL_CONTAINER_TEXT('Deno')
    },
  },

  [ServiceStackTypes.Java]: {
    [ScalingTypes.Cpu]: {
      title: 'Java vertical CPU cores scaling',
      desc: 'Java uses a multi-threaded model with support for concurrency, making it capable of utilizing multiple CPU cores effectively. For compute-intensive applications, scaling vertically by adding more cores can significantly improve performance.',
    },
    [ScalingTypes.Ram]: {
      title: 'Java' + RAM_TEXT,
      desc: 'Java applications typically run on the Java Virtual Machine (JVM), which can manage memory dynamically within the limits set at startup. To ensure optimal performance, allocate sufficient RAM to handle peak memory usage and configure JVM options appropriately.',
    },
    [ScalingTypes.Disc]: {
      title: 'Java' + DISC_TEXT,
      desc: GENERAL_DISC_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'Java' + CONTAINER_TEXT,
      desc: 'The best way to scale Java applications is by adding and removing containers. Start with at least two to achieve high availability. However, ensure that your application is designed to handle distributed workloads and can manage state across multiple instances. While vertical scaling by adding more CPU cores is effective, horizontal scaling provides better fault tolerance and scalability for high-availability systems.',
    },
  },

  [ServiceStackTypes.DotNet]: {
    [ScalingTypes.Cpu]: {
      title: '.NET vertical CPU cores scaling',
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: '.NET' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: '.NET' + DISC_TEXT,
      desc: GENERAL_DISC_TEXT
    },
    [ScalingTypes.Container]: {
      title: '.NET' + CONTAINER_TEXT,
      desc: GENERAL_CONTAINER_TEXT('.NET')
    },
  },

  [ServiceStackTypes.Golang]: {
    [ScalingTypes.Cpu]: {
      title: 'Golang vertical CPU cores scaling',
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Golang' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Golang' + DISC_TEXT,
      desc: GENERAL_DISC_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'Golang' + CONTAINER_TEXT,
      desc: GENERAL_CONTAINER_TEXT('Golang')
    },
  },

  [ServiceStackTypes.Python]: {
    [ScalingTypes.Cpu]: {
      title: 'Python vertical CPU cores scaling',
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Python' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Python' + DISC_TEXT,
      desc: GENERAL_DISC_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'Python' + CONTAINER_TEXT,
      desc: GENERAL_CONTAINER_TEXT('Python')
    },
  },

  [ServiceStackTypes.Rust]: {
    [ScalingTypes.Cpu]: {
      title: 'Rust vertical CPU cores scaling',
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Rust' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Rust' + DISC_TEXT,
      desc: GENERAL_DISC_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'Rust' + CONTAINER_TEXT,
      desc: GENERAL_CONTAINER_TEXT('Rust')
    },
  },

  [ServiceStackTypes.Ubuntu]: {
    [ScalingTypes.Cpu]: {
      title: 'Ubuntu' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Ubuntu' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Ubuntu' + DISC_TEXT,
      desc: GENERAL_DISC_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'Ubuntu' + CONTAINER_TEXT,
      desc: 'Start with at least two to achieve high availability. Remember, however, that your application needs to be prepared for this.'
    },
  },

  [ServiceStackTypes.Alpine]: {
    [ScalingTypes.Cpu]: {
      title: 'Alpine' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Alpine' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Alpine' + DISC_TEXT,
      desc: GENERAL_DISC_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'Alpine' + CONTAINER_TEXT,
      desc: 'Start with at least two to achieve high availability. Remember, however, that your application needs to be prepared for this.',
    }
  },

  [ServiceStackTypes.Docker]: {
    [ScalingTypes.Cpu]: {
      title: 'Docker cores configuration',
      desc: 'Virtual machines do not support vertical scaling, set the number of cores your Docker images require.'
    },
    [ScalingTypes.Ram]: {
      title: 'Docker RAM configuration',
      desc: 'Virtual machines do not support vertical scaling, set the amount of RAM your Docker images require.'
    },
    [ScalingTypes.Disc]: {
      title: 'Docker Disk configuration',
      desc: 'Virtual machines do not support vertical scaling, set the amount of Disk your Docker images require. You, however, should not use the local disk to store your files as the containers are volatile, and their data are not mirrored across the cluster nor backup-ed. Use one of the Zerops storage services to keep your files permanently.',
    },
    [ScalingTypes.Container]: {
      title: 'Docker' + CONTAINER_TEXT,
      desc: 'Set the amount of identical instances of your Docker images'
    }
  },

  [ServiceStackTypes.ZBox]: {
    [ScalingTypes.Cpu]: {
      title: 'Box Workspace cores configuration',
      desc: 'Virtual machines do not support vertical scaling, set the number of cores your workspace requires.'
    },
    [ScalingTypes.Ram]: {
      title: 'Box Workspace RAM configuration',
      desc: 'Virtual machines do not support vertical scaling, set the amount of RAM your workspace requires.'
    },
    [ScalingTypes.Disc]: {
      title: 'Box Workspace Disk configuration',
      desc: 'Virtual machines do not support vertical scaling, set the amount of Disk your workspace requires. The workspace runs as a single persistent virtual machine, its disk is kept across restarts but is not mirrored across the cluster nor backup-ed.',
    },
    [ScalingTypes.Container]: {
      title: 'Box Workspace' + CONTAINER_TEXT,
      desc: 'A Box Workspace always runs as a single persistent virtual machine and cannot be scaled horizontally.'
    }
  },

  [ServiceStackTypes.Nginx]: {
    [ScalingTypes.Cpu]: {
      title: 'Nginx' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Nginx' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Nginx' + DISC_TEXT,
      desc: DISC_SPACE_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'Nginx' + CONTAINER_TEXT,
      desc: GENERAL_CONTAINER_TEXT('Nginx')
    },
  },

  [ServiceStackTypes.PhpApache]: {
    [ScalingTypes.Cpu]: {
      title: 'PHP+Apache' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'PHP+Apache' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'PHP+Apache' + DISC_TEXT,
      desc: DISC_SPACE_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'PHP+Apache' + CONTAINER_TEXT,
      desc: GENERAL_CONTAINER_TEXT('PHP+Apache')
    },
  },

  [ServiceStackTypes.PhpNginx]: {
    [ScalingTypes.Cpu]: {
      title: 'PHP+Nginx' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'PHP+Nginx' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'PHP+Nginx' + DISC_TEXT,
      desc: DISC_SPACE_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'PHP+Nginx' + CONTAINER_TEXT,
      desc: GENERAL_CONTAINER_TEXT('PHP+Nginx')
    },
  },

  [ServiceStackTypes.Static]: {
    [ScalingTypes.Cpu]: {
      title: 'Static app' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Static app' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Static app' + DISC_TEXT,
      desc: DISC_SPACE_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'Static app' + CONTAINER_TEXT,
      desc: GENERAL_CONTAINER_TEXT('Static app')
    },
  },

  [ServiceStackTypes.Elasticsearch]: {
    [ScalingTypes.Cpu]: {
      title: 'Elasticsearch' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Elasticsearch' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Elasticsearch' + DISC_TEXT,
      desc: DISC_SPACE_TEXT
    },
  },

  [ServiceStackTypes.Meilisearch]: {
    [ScalingTypes.Cpu]: {
      title: 'Meilisearch' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Meilisearch' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Meilisearch' + DISC_TEXT,
      desc: DISC_SPACE_TEXT
    },
  },

  [ServiceStackTypes.Typesense]: {
    [ScalingTypes.Cpu]: {
      title: 'Typesense' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Typesense' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Typesense' + DISC_TEXT,
      desc: DISC_SPACE_TEXT
    },
  },

  [ServiceStackTypes.Redis]: {
    [ScalingTypes.Cpu]: {
      title: 'KeyDB' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'KeyDB' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'KeyDB' + DISC_TEXT,
      desc: DISC_SCALING_TEXT
    },
  },

  [ServiceStackTypes.Postgres]: {
    [ScalingTypes.Cpu]: {
      title: 'PostgreSQL' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'PostgreSQL' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'PostgreSQL' + DISC_TEXT,
      desc: DISC_SCALING_TEXT
    },
  },

  [ServiceStackTypes.Clickhouse]: {
    [ScalingTypes.Cpu]: {
      title: 'ClickHouse' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'ClickHouse' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'ClickHouse' + DISC_TEXT,
      desc: DISC_SCALING_TEXT
    },
  },

  [ServiceStackTypes.Valkey]: {
    [ScalingTypes.Cpu]: {
      title: 'Valkey' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Valkey' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Valkey' + DISC_TEXT,
      desc: DISC_SCALING_TEXT
    },
  },

  [ServiceStackTypes.Qdrant]: {
    [ScalingTypes.Cpu]: {
      title: 'Qdrant' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Qdrant' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Qdrant' + DISC_TEXT,
      desc: DISC_SCALING_TEXT
    },
  },

  [ServiceStackTypes.Nats]: {
    [ScalingTypes.Cpu]: {
      title: 'NATS' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'NATS' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'NATS' + DISC_TEXT,
      desc: DISC_SCALING_TEXT
    },
  },

  [ServiceStackTypes.Kafka]: {
    [ScalingTypes.Cpu]: {
      title: 'Apache Kafka' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Apache Kafka' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Apache Kafka' + DISC_TEXT,
      desc: DISC_SCALING_TEXT
    },
  },

  [ServiceStackTypes.RabbitMq]: {
    [ScalingTypes.Cpu]: {
      title: 'RabbitMQ' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'RabbitMQ' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'RabbitMQ' + DISC_TEXT,
      desc: DISC_SCALING_TEXT
    },
  },

  [ServiceStackTypes.MariaDb]: {
    [ScalingTypes.Cpu]: {
      title: 'Mariadb' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Mariadb' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Mariadb' + DISC_TEXT,
      desc: DISC_SCALING_TEXT
    },
  },

  [ServiceStackTypes.MongoDb]: {
    [ScalingTypes.Cpu]: {
      title: 'MongoDB' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'MongoDB' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'MongoDB' + DISC_TEXT,
      desc: 'Disk space can be scaled up and down without restarting the process, you however should not use Disk to store your files as the containers are volatile and their data not synchronized. Use one of the storage services to store your files.',
    },
  },

  [ServiceStackTypes.Seaweedfs]: {
    [ScalingTypes.Cpu]: {
      title: 'SeaweedFS' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'SeaweedFS' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'SeaweedFS' + DISC_TEXT,
      desc: DISC_SPACE_TEXT,
    },
  },

  [ServiceStackTypes.LocalStorage]: {
    [ScalingTypes.Cpu]: {
      title: 'Local storage' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Local storage' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Local storage' + DISC_TEXT,
      desc: LOCAL_STORAGE_DISC_TEXT,
    },
  },

  [ServiceStackTypes.Zcp]: {
    [ScalingTypes.Cpu]: {
      title: 'Zerops Control Plane' + CPU_TEXT,
      desc: CPU_SCALE_TEXT
    },
    [ScalingTypes.Ram]: {
      title: 'Zerops Control Plane' + RAM_TEXT,
      desc: RAM_ADJUST_TEXT
    },
    [ScalingTypes.Disc]: {
      title: 'Zerops Control Plane' + DISC_TEXT,
      desc: GENERAL_DISC_TEXT
    },
    [ScalingTypes.Container]: {
      title: 'Zerops Control Plane' + CONTAINER_TEXT,
      desc: GENERAL_CONTAINER_TEXT('Zerops Control Plane')
    },
  },
};
